import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { addLeavePlanToBackend, updateLeavePlanToBackend, loadMembersFromBackendForClient } from "app/services/member.service";
import { loadMembersForManagerCached, loadScopeManagersForIds } from "app/services/manager.service";
import { decodeOwnerValue, computeManagesSelectedClient } from "app/components/reports/AddActivityDialog";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import { HolidayDateNudge } from "app/components/common/HolidayDateNudge";
import * as AppConstants from "AppConstants";
import MDBox from "components/MDBox";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getDateInFormat } from "utils/dateUtil";
import { toManagerClientOptions, toMemberClientOptions, toClientListOptions, mergeClientOptionsById } from "utils/clientOptions";
import { sortByName, sortByText } from "utils/optionSort";
import { validateLeavePlanObject } from "utils/leavePlanValidationUtil";
import { parseReportRowEntityId } from "utils/reportRowId";
import {
  readSessionCurrentMember,
  getCurrentManager,
  readSessionCurrentClient,
  readSessionCurrentManager,
  getMemberClients,
} from "app/services/app.state.service";
import { DROPDOWN_SLOT_PROPS } from "utils/dropdownSlotProps";
import { useDispatch, useSelector } from "react-redux";
import "./AddLeaveDialog.css";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

// Who the leave is being filed for. Managers default to MEMBER (file for a team member);
// SELF files the leave against the logged-in manager (isMember=false, managerId = the manager).
const LEAVE_TARGET_MEMBER = "MEMBER";
const LEAVE_TARGET_SELF = "SELF";

// Prefix for manager-owned owner selections in the owner dropdown (on-behalf).
const MANAGER_OPTION_PREFIX = "MGR-";

// The name shown for an on-behalf manager option. Shared by the sort and the render so the
// two cannot drift: ordering on a different expression than the one displayed would put a
// record with no name on file (shown as "Manager 42") at the top of the group.
// @since NEXT_VERSION
const l1ManagerLabel = (mgr) => mgr.managerName ?? mgr.name ?? `Manager ${mgr.id}`;

// Stable default for the cache prop so an omitted prop doesn't create a new
// object each render (which would thrash the member effect).
const EMPTY_CACHE = {};

const obj = {
  memberId: null,
  reason: "",
  startDate: getDateInFormat(new Date()),
  endDate: getDateInFormat(new Date()),
  status: AppConstants.DEFAULT_LEAVE_PLAN_STATUS,
  type: AppConstants.DEFAULT_LEAVE_PLAN_TYPE,
  leaveDuration: AppConstants.DEFAULT_LEAVE_DURATION,
  isMember: true,
  managerId: null,
};

const AddLeaveDialog = ({
  role,
  addLeaveOpen,
  setAddLeaveOpen,
  refreshActivitiesDataHandler,
  editObject,
  // Per-client members cache warmed on dashboard load. Read this before hitting
  // the network so changing the client dropdown doesn't re-fetch.
  membersByClient = EMPTY_CACHE,
}) => {
  const parseNullableInt = (value) => {
    const parsed = parseInt(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const dispatch = useDispatch();
  // Session cache keyed by manager_id, read before hitting by-manager/{id}.
  const membersByManager = useSelector((state) => state.client?.membersByManager || EMPTY_CACHE);

  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const [leaveTarget, setLeaveTarget] = useState(LEAVE_TARGET_MEMBER);
  const [selfManagerId, setSelfManagerId] = useState(null);
  const [resolvingSelf, setResolvingSelf] = useState(false);
  // L2: all managers in this manager's subtree (self + L1 children); empty for L1s.
  const [subtreeManagers, setSubtreeManagers] = useState([]);
  // Self-target is only meaningful for managers — admins have no _manager row.
  // Enabled for both add and edit: in edit mode the radio is shown locked so a
  // manager's own leave reads as "Myself", and the self-profile + subtree
  // resolution it gates must run so the saved member / on-behalf manager can be
  // shown in the (disabled) dropdown.
  const selfTargetEnabled = role === AppConstants.ROLE_EMPLOYEE_MANAGER;

  // Dialog-local client selection. The dialog owns its client context explicitly
  // instead of deriving it from a global filter.
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientOptions, setClientOptions] = useState([]); // [{ id, label }]
  const [dialogMembers, setDialogMembers] = useState([]); // members for selectedClientId (admin/manager)
  const [memberClientsList, setMemberClientsList] = useState([]); // member-role memberships

  // On open, build the client dropdown options and pick the initial client for
  // the current role. Edit starts from the row's client; add starts from the
  // first available client.
  useEffect(() => {
    if (!addLeaveOpen) return;
    const initialFromObject = editObject?.clientId ?? null;

    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) {
      getMemberClients().then((list) => {
        const memberships = Array.isArray(list) ? list : [];
        setMemberClientsList(memberships);
        setClientOptions(toMemberClientOptions(memberships));
        setSelectedClientId(String(initialFromObject ?? memberships[0]?.client?.id ?? ""));
      });
      return;
    }

    let optionList = [];
    if (role === AppConstants.ROLE_EMPLOYEE_MANAGER) {
      const list = readSessionCurrentManager();
      optionList = toManagerClientOptions(list);
    } else {
      const list = readSessionCurrentClient();
      optionList = toClientListOptions(list);
    }
    setClientOptions(optionList);
    setSelectedClientId(String(initialFromObject ?? optionList[0]?.id ?? ""));
  }, [addLeaveOpen, role, editObject]);

  // For the manager role: once the subtree loads, union its clients (incl. L1s') with the
  // manager's own session assignments so every client they can log against stays selectable.
  useEffect(() => {
    if (role !== AppConstants.ROLE_EMPLOYEE_MANAGER) return;
    if (subtreeManagers.length === 0) return;

    const subtreeClientOptions = Object.values(
      subtreeManagers.reduce((acc, m) => {
        if (m?.clientId != null && !acc[m.clientId]) {
          acc[m.clientId] = {
            id: String(m.clientId),
            label:
              m.clientCode && m.clientName
                ? `${m.clientCode} - ${m.clientName}`
                : m.clientName ?? m.clientCode ?? String(m.clientId),
          };
        }
        return acc;
      }, {})
    );

    const mergedClientOptions = mergeClientOptionsById(
      subtreeClientOptions,
      toManagerClientOptions(readSessionCurrentManager())
    );
    if (mergedClientOptions.length > 0) {
      setClientOptions(mergedClientOptions);
      // Keep the current selection if still valid; otherwise default to the first client.
      setSelectedClientId((prev) => {
        const stillValid = mergedClientOptions.some((o) => String(o.id) === String(prev));
        return stillValid ? prev : String(mergedClientOptions[0]?.id ?? prev);
      });
    }
  }, [subtreeManagers, role]);

  // Initialize the form once each time the dialog opens; guarded with a ref so
  // async data (dialogMembers loading in) doesn't re-run it and wipe user input.
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!addLeaveOpen) {
      initializedRef.current = false;
      // Reset the target so a prior self-edit's SELF doesn't leak into the next open
      // (which would flash the Myself radio and clobber the restored member owner).
      setLeaveTarget(LEAVE_TARGET_MEMBER);
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    setMessageOpen(false);
    setMessage("");
    setLeaveTarget(LEAVE_TARGET_MEMBER);

    if (editObject) {
      // Restore the owner type. A manager-owned row carries isMember === false with
      // the manager_id in memberId, so preserve it as managerId (not memberId) —
      // otherwise the manager reads as a member whose id matches nothing and the
      // dropdown blanks. When the row is one of the logged-in manager's own records,
      // pre-select "Myself"; an L1 in the subtree stays "Team member" (MGR-<id>).
      const editIsMember = editObject.isMember !== false;
      const ownManagerIds = (readSessionCurrentManager() || []).map((r) => String(r?.id));
      const isOwnManagerRow = !editIsMember && ownManagerIds.includes(String(editObject.memberId));
      setLeaveTarget(isOwnManagerRow ? LEAVE_TARGET_SELF : LEAVE_TARGET_MEMBER);
      setData({
        ...obj,
        isMember: editIsMember,
        memberId: editIsMember ? parseNullableInt(editObject.memberId) : null,
        managerId: editIsMember ? null : parseNullableInt(editObject.memberId),
        startDate: editObject.startDate,
        endDate: editObject.endDate,
        reason: editObject.description,
        // The report row omits leaveDuration; the caller fetches the plan and puts
        // it on editObject so the dialog opens with the saved half-day already set.
        leaveDuration: editObject.leaveDuration ?? AppConstants.DEFAULT_LEAVE_DURATION,
      });
      return;
    }

    let memberId = null;
    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) {
      const member = readSessionCurrentMember();
      memberId = parseNullableInt(member?.id);
    } else if (dialogMembers.length > 0 && dialogMembers[0]?.id) {
      memberId = parseNullableInt(dialogMembers[0].id);
    }
    setData({ ...obj, memberId });
  }, [addLeaveOpen, role, dialogMembers, editObject]);

  // Admin/manager: the client dropdown filters the member dropdown. Load the
  // selected client's members for the dropdown. A manager sees only the members
  // reporting to the manager record for the selected client (which may be an L1
  // in the subtree for an L2 user); an admin sees every member of the client. In
  // a fresh add we also default the memberId to the first member; in edit the
  // memberId is already carried over from the source row.
  useEffect(() => {
    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) return;
    // Editing shows a single locked option (the row's memberName), so the member
    // list is never read — skip the fetch. (The client field's label comes from the
    // session/subtree client options, not from this list.)
    if (editObject) return;
    if (!selectedClientId) return;

    const applyMembers = (unsorted) => {
      // Sorted here rather than at render, so the default owner picked below is the first
      // name the user sees rather than the client's oldest member record.
      const list = sortByName(unsorted);
      setDialogMembers(list);
      // On a fresh add, switching client resets the owner to that client's first member.
      // Also clear any on-behalf manager selection (isMember=false/managerId) carried over
      // from the previous client so a stale L1 can't be submitted against the new client.
      // "Myself" is left untouched here — the self-fallback effect re-evaluates it per client.
      if (!editObject && leaveTarget !== LEAVE_TARGET_SELF) {
        setData((prev) => ({
          ...prev,
          isMember: true,
          managerId: null,
          memberId: parseNullableInt(list[0]?.id),
        }));
      }
    };

    // The per-client cache holds one manager's members; for a manager it can't represent
    // a client with several subtree managers (the L2 + its L1s), so use it only for others.
    const cached =
      role !== AppConstants.ROLE_EMPLOYEE_MANAGER ? membersByClient[String(selectedClientId)] : undefined;
    if (cached) {
      applyMembers(cached);
      return;
    }

    if (role === AppConstants.ROLE_EMPLOYEE_MANAGER) {
      // A selected client can have several subtree managers (the L2 and/or its L1s).
      // Aggregate every such manager's members so the L1s' members show too. Fall back to
      // the L2's own record while the subtree hasn't loaded.
      const managersForClient = subtreeManagers.filter(
        (m) => String(m.clientId) === String(selectedClientId)
      );
      const ids =
        managersForClient.length > 0
          ? managersForClient.map((m) => parseNullableInt(m.id))
          : [managerIdForSelectedClient()].filter((id) => id != null);
      if (ids.length === 0) {
        applyMembers([]);
        return;
      }
      // Fetch each subtree manager's members through the cache, then aggregate and de-dupe.
      Promise.all(
        ids.map((id) => loadMembersForManagerCached(id, membersByManager, dispatch))
      ).then((lists) => {
        const byId = new Map(lists.flat().map((m) => [m.id, m]));
        applyMembers(Array.from(byId.values()));
      });
      return;
    }

    loadMembersFromBackendForClient(selectedClientId)
      .then((r) => applyMembers(r.status === 204 ? [] : r.data))
      .catch(() => setDialogMembers([]));
    // membersByManager is read but omitted from deps: the dialog writes to it, so
    // depending on it would self-trigger a re-run (client/subtree changes suffice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, role, editObject, membersByClient, subtreeManagers]);

  // Member role: set the memberId to the membership whose client matches the
  // chosen client.
  useEffect(() => {
    if (role !== AppConstants.ROLE_EMPLOYEE_MEMBER) return;
    if (!selectedClientId) return;
    const membership = memberClientsList.find(
      (m) => String(m.client.id) === String(selectedClientId)
    );
    if (membership) setData((prev) => ({ ...prev, memberId: parseNullableInt(membership.id) }));
  }, [selectedClientId, role, memberClientsList]);

  // Resolve the logged-in manager's manager_id so they can file their own leave.
  // Backend treats leave with isMember=false as manager-owned (memberId null, managerId = actor).
  // If resolution fails, the "Myself" option stays disabled.
  useEffect(() => {
    if (!addLeaveOpen || !selfTargetEnabled) return;

    let cancelled = false;
    setResolvingSelf(true);
    getCurrentManager()
      .then((manager) => {
        if (!cancelled) setSelfManagerId(parseNullableInt(manager?.id));
      })
      .catch(() => {
        if (!cancelled) setSelfManagerId(null);
      })
      .finally(() => {
        if (!cancelled) setResolvingSelf(false);
      });

    return () => {
      cancelled = true;
    };
  }, [addLeaveOpen, selfTargetEnabled]);

  // The logged-in person's own manager record ids (one per client), plus the resolved
  // record. "Self" is this whole set, so a client managed through any of the L2's records
  // counts as directly managed and an L1 under any record stays in scope.
  const selfManagerIds = useMemo(() => {
    const ids = (readSessionCurrentManager() || []).map((c) => c?.id).filter((id) => id != null);
    return selfManagerId != null && !ids.includes(selfManagerId) ? [...ids, selfManagerId] : ids;
  }, [selfManagerId]);

  // Load the subtree of managers (self + L1 children) for the L2 hierarchy flow, rolled
  // up across ALL the manager's records — not just the resolved first one — so an L1
  // reporting through any of the L2's records is included. An L1 gets just themselves.
  useEffect(() => {
    if (!addLeaveOpen || !selfTargetEnabled || !selfManagerId) {
      setSubtreeManagers([]);
      return;
    }
    loadScopeManagersForIds(selfManagerIds)
      .then((list) => setSubtreeManagers(list))
      .catch(() => setSubtreeManagers([]));
  }, [addLeaveOpen, selfTargetEnabled, selfManagerId, selfManagerIds]);

  // For self-leave, the actor is the manager record tied to the selected client
  // (a manager may manage several). Falls back to the resolved selfManagerId if
  // no record matches (e.g. before options have loaded).
  const managerIdForSelectedClient = () => {
    const list = readSessionCurrentManager();
    const rec = (Array.isArray(list) ? list : []).find(
      (c) => String(c.clientId) === String(selectedClientId)
    );
    return rec ? parseNullableInt(rec.id) : selfManagerId;
  };

  // True when one of the L2's own records covers the selected client. Gates the "Myself"
  // radio so the user can't log self-leave against a client they don't directly manage.
  const managesSelectedClient = computeManagesSelectedClient(subtreeManagers, selfManagerIds, selectedClientId);

  // "Myself" is enabled only for managers (selfTargetEnabled) who have resolved
  // their manager profile (selfManagerId) AND the selected client is one they
  // directly manage.
  const selfRadioEnabled = selfTargetEnabled && Boolean(selfManagerId) && managesSelectedClient;

  // Owner dropdown handler for the manager role: decodes member IDs and the
  // MGR-<id> sentinel for on-behalf L1 manager leave.
  const handleOwnerSelect = (value) => {
    const decoded = decodeOwnerValue(value, parseNullableInt);
    // decoded uses ownerId field — map it to memberId for leave
    setLeaveTarget(LEAVE_TARGET_MEMBER); // dropdown drives owner, not Self
    setData((prev) => ({
      ...prev,
      isMember: decoded.isMember,
      managerId: decoded.managerId,
      memberId: decoded.ownerId,
    }));
  };

  // Keep the resolved manager id on the form while targeting "Myself". Covers
  // both the radio selection and the initial sync when the dialog opens.
  useEffect(() => {
    if (editObject) return; // edit: owner is locked and fully set by init; never re-sync
    if (!addLeaveOpen || leaveTarget !== LEAVE_TARGET_SELF) return;
    const managerId = managerIdForSelectedClient();
    setData((prev) =>
      prev.isMember === false && prev.managerId === managerId
        ? prev
        : { ...prev, isMember: false, memberId: null, managerId }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLeaveOpen, leaveTarget, selfManagerId, selectedClientId]);

  // If the user switches to a client they don't directly manage while "Myself"
  // is selected, fall back to "Team member" so the form stays valid.
  // Only fires once the subtree has loaded (subtreeManagers non-empty) to avoid
  // a false-positive fallback on initial mount before the async fetch completes.
  useEffect(() => {
    if (editObject) return; // edit: owner is locked, never auto-flip the target
    if (subtreeManagers.length === 0) return; // subtree not yet loaded; don't act yet
    if (leaveTarget === LEAVE_TARGET_SELF && !selfRadioEnabled) {
      handleLeaveTargetChange(LEAVE_TARGET_MEMBER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selfRadioEnabled, subtreeManagers]);

  const handleLeaveTargetChange = (target) => {
    setLeaveTarget(target);
    if (target === LEAVE_TARGET_SELF) {
      setData((prev) => ({ ...prev, isMember: false, managerId: managerIdForSelectedClient(), memberId: null }));
    } else {
      setData((prev) => ({
        ...prev,
        isMember: true,
        managerId: null,
        memberId: parseNullableInt(dialogMembers[0]?.id),
      }));
    }
  };


  // Half-day applies only to single-day leave; multi-day is always FULL_DAY.
  const isMultiDayLeave = data.startDate !== data.endDate;

  const addLeaveInBackend = async () => {
    const payload = trimTextFields({
      ...data,
      leaveDuration: isMultiDayLeave ? AppConstants.DEFAULT_LEAVE_DURATION : data.leaveDuration,
    });
    if (validateLeavePlanObject(payload) && !editObject) await addLeaveInvoker(payload);
    else if (validateLeavePlanObject(payload) && editObject) await updateLeaveInvoker(payload);
    else {
      setErrorAction(
        "One or more required fields for a Leave are missing or having invalid value"
      );
    }
  };

  const addLeaveInvoker = async (activityObject) => {
    await addLeavePlanToBackend(activityObject)
      .then((data) => {
        setSuccessAction("Leave added successfully");
        setTimeout(()=> {
          setData(obj);
          setAddLeaveOpen(false);
          setMessage("");
          setMessageOpen(false);
          refreshActivitiesDataHandler();
        }, 1000)
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const updateLeaveInvoker = async (data) => {
    if (!editObject?.id) {
      setErrorAction("Unable to update leave plan. Missing identifier.");
      return;
    }

    const editObjectId = parseReportRowEntityId(editObject.id);
    if (!editObjectId) {
      setErrorAction("Unable to update leave plan. Invalid identifier.");
      return;
    }

    await updateLeavePlanToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Leave Updated successfully");
        setTimeout(() => {
          setData(obj);
          setAddLeaveOpen(false);
          setMessage("");
          setMessageOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  }

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  // The owner dropdown's current value: MGR-<id> for an on-behalf manager, else the member id.
  const ownerFieldValue =
    role === AppConstants.ROLE_EMPLOYEE_MANAGER && !data.isMember && data.managerId
      ? `${MANAGER_OPTION_PREFIX}${data.managerId}`
      : (data.memberId ?? "");

  return (
    <Dialog open={addLeaveOpen} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Leave` : `Add New Leave`}</DialogTitle>
      <DialogContent>
        {clientOptions.length > 0 &&
          (editObject ? (
            // Editing an existing leave: its client is fixed, so show a disabled field.
            <MDBox my={2}>
              <TextField
                select
                fullWidth
                disabled
                variant="outlined"
                id="add-leave-client"
                label="Client"
                value={selectedClientId ?? ""}
                sx={{ width: "100%" }}
              >
                {clientOptions.filter(Boolean).map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </MDBox>
          ) : (
            <MDBox my={2}>
              <ClientFilterSelect
                value={selectedClientId}
                onChange={setSelectedClientId}
                options={clientOptions}
                includeAll={false}
                label="Client"
                id="add-leave-client"
                sx={{ width: "100%" }}
              />
            </MDBox>
          ))}
        {selfTargetEnabled && (
          <MDBox my={2}>
            <FormControl>
              <FormLabel id="leave-target-label" className="add-leave-label">Leave for</FormLabel>
              <RadioGroup
                row
                aria-labelledby="leave-target-label"
                name="leave-target"
                value={leaveTarget}
                onChange={(e) => handleLeaveTargetChange(e.target.value)}
              >
                <FormControlLabel
                  value={LEAVE_TARGET_SELF}
                  control={<Radio />}
                  label="Myself"
                  disabled={Boolean(editObject) || resolvingSelf || !selfRadioEnabled}
                />
                <FormControlLabel
                  value={LEAVE_TARGET_MEMBER}
                  control={<Radio />}
                  label="Team member"
                  disabled={Boolean(editObject)}
                />
              </RadioGroup>
              {/* Add-only hints: the owner is locked while editing, so they don't apply. */}
              {!editObject && !resolvingSelf && !selfManagerId && (
                <FormHelperText>Manager profile not resolved for your account</FormHelperText>
              )}
              {/* Wait for the subtree to load (non-empty) before judging "manages"; otherwise
                  this flashes while the subtree fetch is still in flight after selfManagerId
                  resolves — the subtree always holds ≥1 of the manager's own records. */}
              {!editObject && !resolvingSelf && selfManagerId && subtreeManagers.length > 0 && !managesSelectedClient && (
                <FormHelperText>You do not directly manage the selected client</FormHelperText>
              )}
            </FormControl>
          </MDBox>
        )}
        {role !== AppConstants.ROLE_EMPLOYEE_MEMBER && leaveTarget === LEAVE_TARGET_MEMBER && (
        <MDBox my={2}>
          {editObject ? (
            // Editing never reassigns the owner, so render the saved name as plain
            // read-only text instead of a locked <TextField select>. A select only paints
            // its display when its value both matches a MenuItem and is non-empty; for an
            // admin editing a manager-owned row the owner id lives in managerId (memberId
            // is null by design), so the value fell back to "" and the field rendered
            // blank. Text has no value-to-option coupling.
            <TextField
              fullWidth
              disabled
              variant="outlined"
              label={role === AppConstants.ROLE_EMPLOYEE_MANAGER ? "Member / Manager" : "Select Member"}
              id="member"
              value={editObject.memberName ?? ""}
            />
          ) : (
          <TextField
            select
            autoFocus
            fullWidth
            variant="outlined"
            label={role === AppConstants.ROLE_EMPLOYEE_MANAGER ? "Member / Manager" : "Select Member"}
            id="member"
            value={ownerFieldValue}
            onChange={(e) =>
              role === AppConstants.ROLE_EMPLOYEE_MANAGER
                ? handleOwnerSelect(e.target.value)
                : setData({ ...data, memberId: parseInt(e.target.value, 10) || null })
            }
            helperText="*Required"
            slotProps={DROPDOWN_SLOT_PROPS}
          >
            {/* For the manager role, render Members + (optional) Managers optgroups */}
            {role === AppConstants.ROLE_EMPLOYEE_MANAGER ? (
              (() => {
                // L1 managers for the selected client (exclude all the L2's own records)
                const selfIdSet = new Set(selfManagerIds.map(String));
                const l1ManagersForClient = sortByText(
                  subtreeManagers.filter(
                    (m) =>
                      String(m.clientId) === String(selectedClientId) &&
                      !selfIdSet.has(String(m.id))
                  ),
                  l1ManagerLabel
                );
                const memberItems = [
                  <MenuItem key="__placeholder" value="">
                    <em>Select a member</em>
                  </MenuItem>,
                  ...dialogMembers?.filter(Boolean).map((member) => (
                    <MenuItem key={`mbr-${member.id}`} value={member.id}>
                      {member.name}
                    </MenuItem>
                  )),
                ];
                if (l1ManagersForClient.length === 0) {
                  // No L1s for this client — just show the flat member list (L1 path)
                  return memberItems;
                }
                return [
                  <MenuItem key="__grp-members" disabled sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.8rem" }}>
                    Members
                  </MenuItem>,
                  ...dialogMembers?.filter(Boolean).map((member) => (
                    <MenuItem key={`mbr-${member.id}`} value={member.id}>
                      {member.name}
                    </MenuItem>
                  )),
                  <MenuItem key="__grp-managers" disabled sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.8rem" }}>
                    Managers (on behalf)
                  </MenuItem>,
                  ...l1ManagersForClient.map((mgr) => (
                    <MenuItem key={`mgr-${mgr.id}`} value={`${MANAGER_OPTION_PREFIX}${mgr.id}`}>
                      {l1ManagerLabel(mgr)}
                    </MenuItem>
                  )),
                ];
              })()
            ) : (
              [
                <MenuItem key="__placeholder" value="">
                  <em>Select a member</em>
                </MenuItem>,
                ...dialogMembers?.filter(Boolean).map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                  </MenuItem>
                )),
              ]
            )}
          </TextField>
          )}
        </MDBox>
        )}
        <MDBox my={2} display="flex" gap={2}>
          <MDBox flex={1} className="add-leave-date-input">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={data.startDate ? dayjs(data.startDate) : null}
                onChange={(newStartDate) => {
                  setData({
                    ...data,
                    startDate: getDateInFormat(newStartDate),
                    endDate: getDateInFormat(newStartDate),
                  });
                }}
                format={AppConstants.DATE_FORMAT_DISPLAY}
              />
            </LocalizationProvider>
          </MDBox>
          <MDBox flex={1} className="add-leave-date-input">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={data.endDate ? dayjs(data.endDate) : null}
                onChange={(newEndDate) => {
                  setData({ ...data, endDate: getDateInFormat(newEndDate) });
                }}
                format={AppConstants.DATE_FORMAT_DISPLAY}
              />
            </LocalizationProvider>
          </MDBox>
        </MDBox>
        {!editObject && (
          <HolidayDateNudge
            startDate={data.startDate}
            endDate={data.endDate}
            ownerId={data.isMember ? data.memberId : data.managerId}
            isMember={data.isMember}
            onAvailed={() => {
              setAddLeaveOpen(false);
              refreshActivitiesDataHandler();
            }}
          />
        )}
        <MDBox mt={4} mb={2}>
          <TextField
            id="reason"
            label="Reason"
            fullWidth
            variant="outlined"
            value={data && data.reason}
            onBlur={trimOnBlur(setData, "reason")}
            onChange={(e) => setData({ ...data, reason: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <FormControl>
            <FormLabel id="leave-duration-label" className="add-leave-label">Leave Duration</FormLabel>
            <RadioGroup
              row
              aria-labelledby="leave-duration-label"
              name="leave-duration-group"
              value={isMultiDayLeave ? AppConstants.DEFAULT_LEAVE_DURATION : data.leaveDuration}
              onChange={(e) => setData({ ...data, leaveDuration: e.target.value })}
            >
              {AppConstants.LEAVE_DURATIONS.map((duration) => (
                <FormControlLabel
                  key={duration.value}
                  value={duration.value}
                  control={<Radio />}
                  label={duration.label}
                  // Half-day options apply only to a single-day leave
                  disabled={isMultiDayLeave && duration.value !== AppConstants.DEFAULT_LEAVE_DURATION}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </MDBox>
        <Collapse in={messageOpen}>
                        <Alert
                        action={
                            <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setMessageOpen(false);
                            }}
                            >
                            <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        severity={severity}
                        sx={{ mb: 2 }}
                        >
                        {message}
                        </Alert>
                    </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddLeaveOpen(false)}>Cancel</Button>
        <Button onClick={addLeaveInBackend} variant="contained">{editObject ? `Update` : `Add`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddLeaveDialog);
